import { useCredits } from '@lovable/contexts/CreditContext'
import { Coins, Sparkles } from 'lucide-react'
import { Badge } from '@lovable/components/ui/badge'
import { t } from 'i18next'
import { Select, SelectOption } from '@/common/components/Select/Select'
import { ENGINE_LANGUAGE_SELECT_OPTIONS } from '@/common/constants'
import { SingleValue } from 'react-select'

interface Props {
  language?: SelectOption<string>
  onChangeLanguage?: (selectOption: SingleValue<SelectOption>) => void
}
export const CreditCounter: React.FC<Props> = ({ language, onChangeLanguage }) => {
  const { displayTotalCredits, displayGameCredits, isAnimating } = useCredits()

  return (
    <>
      {onChangeLanguage && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            color: 'var(--grey-700)',
            gap: '16px',
            marginRight: 'auto',
          }}
        >
          {t('onboarding_wizard.language', 'Language')}
          {': '}
          <Select options={ENGINE_LANGUAGE_SELECT_OPTIONS} value={language} onChange={onChangeLanguage} />
          <p className='text-xs text-muted-foreground max-w-[180px]'>
            {t('engine.language_info', 'Game will be created using this language')}
          </p>
        </div>
      )}
      <div className='flex items-center gap-6'>
        <div className='flex items-center gap-4 bg-card border-2 border-border rounded-xl px-4 py-3 shadow-sm'>
          {/* Total Credits */}
          <div className='flex items-center gap-2'>
            <div
              className={`p-2 rounded-lg bg-primary/10 transition-all duration-300 ${isAnimating ? 'scale-110' : ''}`}
            >
              <Coins
                className={`w-5 h-5 text-primary transition-transform duration-300 ${
                  isAnimating ? 'animate-spin' : ''
                }`}
              />
            </div>
            <div className='flex flex-col'>
              <span className='text-xs text-muted-foreground'>{t('engine.total_credits', 'Total Credits')}</span>
              <span
                className={`text-lg font-bold text-foreground tabular-nums transition-all duration-300 ${
                  isAnimating ? 'text-primary scale-110' : ''
                }`}
              >
                {displayTotalCredits}
              </span>
            </div>
          </div>

          {/* Divider */}
          <div className='h-10 w-px bg-border' />

          {/* Current Game Credits */}
          <div className='flex items-center gap-2'>
            <div
              className={`p-2 rounded-lg bg-secondary/10 transition-all duration-300 ${isAnimating ? 'scale-110' : ''}`}
            >
              <Sparkles
                className={`w-5 h-5 text-secondary transition-all duration-300 ${isAnimating ? 'animate-pulse' : ''}`}
              />
            </div>
            <div className='flex flex-col'>
              <span className='text-xs text-muted-foreground'>{t('engine.this_game', 'This Game')}</span>
              <span
                className={`text-lg font-bold text-secondary tabular-nums transition-all duration-300 ${
                  isAnimating ? 'scale-110' : ''
                }`}
              >
                {displayGameCredits}
              </span>
            </div>
          </div>

          {/* Low Credit Warning */}
          {displayTotalCredits < 50 && (
            <Badge variant='outline' className='ml-2 border-warning text-warning animate-pulse'>
              {t('engine.low_credits', 'Low Credits')}
            </Badge>
          )}
        </div>
      </div>
    </>
  )
}
